// The hybrid whole-house configuration: while a Hybrid project has no
// mini-split rooms (utils/system-design-bypass 'hybrid-whole-house'), System
// Design is locked and this configuration stands in for it — one hybrid
// condenser with one ducted zone over every conditioned room and a ducted air
// handler, positioned from the existing equipment placed on Layout & Sizing.
// It lives in the configurations slice like any hand-built configuration, so
// Choose Systems, Add-Ons and the quote run off it unchanged; only the name
// marks it (the same convention as isHybridAutoGeneratedConfigName).
import {
  CondenserLocationType,
  ConfigurationType,
  ExistingSystemType,
  LevelType
} from 'data/types';
import { nanoid } from 'nanoid';
import {
  buildWholeHouseDuctedConfiguration,
  resolveExistingSystemForLayout
} from 'utils/ducted-layout-configuration';
import { WholeHouseDuctedConfigurationType } from 'utils/whole-house-ducted-configuration-types';

export const HYBRID_WHOLE_HOUSE_CONFIG_NAME = 'Hybrid — Whole-House Ducted';
const HYBRID_WHOLE_HOUSE_CONDENSER_NAME = 'Outdoor Unit';
const HYBRID_WHOLE_HOUSE_ZONE_NAME = 'Whole House';

export type BuildHybridWholeHouseConfigurationParams = {
  // The configuration currently standing in, if any — its ids are reused so
  // valid-systems dedupe, comparison references and persistence stay stable.
  current: ConfigurationType | null;
  structure: LevelType[];
  existingSystems: ExistingSystemType[];
  condenserLocations?: readonly CondenserLocationType[] | null;
};

export function isHybridWholeHouseConfiguration(
  configuration: Pick<ConfigurationType, 'name'>
): boolean {
  return configuration.name === HYBRID_WHOLE_HOUSE_CONFIG_NAME;
}

export function findHybridWholeHouseConfiguration(
  configurations: ConfigurationType[]
): ConfigurationType | null {
  return configurations.find(isHybridWholeHouseConfiguration) ?? null;
}

function zoneItemUniqueIdsBySpaceId(
  current: ConfigurationType | null
): Map<string, string> {
  const map = new Map<string, string>();
  current?.condensers.forEach((condenser) =>
    condenser.zones.forEach((zone) =>
      zone.zoneItems.forEach((item) =>
        map.set(item.structureItemId, item.uniqueId)
      )
    )
  );
  return map;
}

export function buildHybridWholeHouseConfiguration({
  current,
  structure,
  existingSystems,
  condenserLocations
}: BuildHybridWholeHouseConfigurationParams): WholeHouseDuctedConfigurationType | null {
  const currentCondenser = current?.condensers[0];
  const currentZone = currentCondenser?.zones[0];
  return buildWholeHouseDuctedConfiguration({
    ids: {
      configurationUniqueId: current?.uniqueId ?? nanoid(5),
      condenserUniqueId: currentCondenser?.uniqueId ?? nanoid(5),
      zoneUniqueId: currentZone?.uniqueId ?? nanoid(5),
      airHandlerUniqueId: currentZone?.airHandler?.uniqueId
    },
    names: {
      configuration: HYBRID_WHOLE_HOUSE_CONFIG_NAME,
      condenser: HYBRID_WHOLE_HOUSE_CONDENSER_NAME,
      zone: HYBRID_WHOLE_HOUSE_ZONE_NAME
    },
    type: 'hybrid',
    existingSystem: resolveExistingSystemForLayout(existingSystems),
    structure,
    condenserLocations,
    zoneItemUniqueIdsBySpaceId: zoneItemUniqueIdsBySpaceId(current)
  });
}

// The parts of the configuration the builder decides: which rooms are zoned
// and where the units sit. Anything else (fetched valid systems, re-synced
// zone loads, line sets) is owned by other watchers and must not trigger a
// rebuild.
export function hybridWholeHouseSignature(
  configuration: ConfigurationType
): string {
  return JSON.stringify(
    configuration.condensers.map((condenser) => ({
      levelUniqueId: condenser.levelUniqueId ?? null,
      position: condenser.position ?? null,
      placementType: condenser.placementType ?? null,
      zones: condenser.zones.map((zone) => ({
        spaceIds: zone.zoneItems
          .map((item) => item.structureItemId)
          .sort((a, b) => a.localeCompare(b)),
        airHandler: zone.airHandler
          ? {
              positionX: zone.airHandler.positionX,
              positionY: zone.airHandler.positionY,
              levelUniqueId: zone.airHandler.levelUniqueId ?? null,
              userPositioned: zone.airHandler.userPositioned ?? false
            }
          : null
      }))
    }))
  );
}
